import React from 'react';
import { useQuery } from '@common/hooks';
import { ColorsType } from '@common/tokens';
import {
  Gap,
  Gutter,
  InfoTooltip,
  Text,
  TimeWithTimeZone,
  Title,
} from '@common/components';
import { BillingCardDeviationAlert } from 'works/legacy/components/billing-card-deviation-alert';
import { ENUM_QUERY } from '@common/graphql';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import {
  ExtensionOfferAssignmentType,
  ExtensionOfferType,
} from '@working-experience/graphql';

const STATE_COLOR_MAP: Record<string, ColorsType> = {
  pending: 'skyRegular',
  active: 'skyRegular',
  change_requested: 'grass',
  retracted: 'sunDarkest',
  accepted: 'meadowDarker',
  rejected: 'coral',
  cancelled: 'coral',
  rejected_by_approver: 'coral',
};

export function ExtensionOfferHeader({
  assignment,
  offer,
}: {
  offer: ExtensionOfferType;
  assignment: ExtensionOfferAssignmentType;
}) {
  const { loading, error, data } = useQuery(ENUM_QUERY, {
    variables: { key: 'OfferRejectionReasons' },
  });

  const rejectionReasonDescriptionByType = (rejectionType?: string) => {
    if (loading || error || !data) return null;
    if (!rejectionType) return null;

    return data.enum?.find(({ name }) => rejectionType === name)?.description;
  };

  return (
    <div className="extension-offer-header">
      {!offer.billingCard && (
        <Gutter direction="bottom" size="md">
          <BillingCardDeviationAlert type="extensionOfferCreated" />
        </Gutter>
      )}
      <Gap vertical>
        <Title size="h4" color={STATE_COLOR_MAP[offer.state]} weight="bold">
          Extension: {stateToLabel(offer.state)}
          {['pending', 'active'].includes(offer.state) && (
            <InfoTooltip
              size="sm"
              icon={faCircleInfo}
              leftOffset={8}
              title={getExtensionMessage(offer.state, assignment.supplier.name)}
            />
          )}
        </Title>
        {offer.state === 'rejected' && (
          <>
            <Text color="coral" block>
              Reason:{' '}
              {rejectionReasonDescriptionByType(
                offer?.rejectionReason || undefined
              )}
            </Text>

            {offer.rejectionDetails && (
              <Text color="coral" block>
                {`"${offer.rejectionDetails}"`}
              </Text>
            )}
          </>
        )}
        {offer.state === 'rejected_by_approver' && offer.rejectionNotes && (
          <Text color="coral" block>
            Notes: {offer.rejectionNotes}
          </Text>
        )}
        <Text secondary block>
          <TimeWithTimeZone
            date={offer.stateChangedAt}
            variant="mid_format_with_tz"
          />
        </Text>
      </Gap>
    </div>
  );
}

function stateToLabel(state: string) {
  switch (state) {
    case 'pending':
      return 'Pending Approval';
    case 'active':
      return 'Sent';
    case 'change_requested':
      return 'Change Requested';
    case 'retracted':
      return 'Retracted';
    case 'rejected':
      return 'Rejected';
    case 'rejected_by_approver':
      return 'Not Approved';
    case 'accepted':
      return 'Accepted';
    case 'cancelled':
      return 'Cancelled';
  }
}

function getExtensionMessage(state: string, supplierName: string) {
  if (state === 'pending') {
    return `Extension has been sent to an approver for review. Once approved, the extension offer will be sent to @${supplierName} for acceptance.`;
  }
  if (state === 'active') {
    return `Extension has been sent to @${supplierName} for acceptance.`;
  }

  return '';
}
